// =============================================
// Link Weaver — "najpierw pisz, potem linkuj" (2026-08-23)
//
// PROBLEM: draft powstawal block-by-block i KAZDY blok H2 dostawal ta sama
// pelna liste linkow z instrukcja "wstaw 1-2" -> 23% artykulow mialo duplikaty
// (270/1158, max 3x ten sam target). Chaining nie pilnuje linkow, a fallback
// Chat Completions widzi poprzednie sekcje ZE ZDJETYMI tagami.
//
// ARCHITEKTURA: po sklejeniu draftu (przed enrichment) JEDEN call LLM
// (step 'link_weaving', gpt-4.1-mini) dostaje pelny HTML + liste targetow
// i wskazuje fragmenty zdan VERBATIM + anchor_text. Owijanie w <a> robi KOD
// deterministycznie (applyLinkPlan) z twardymi walidacjami. LLM proponuje,
// kod decyduje.
//
// Walidacje aplikacji: target max 1x - anchor musi byc substringiem fragmentu
// - fragment tylko w prozie (<p>/<li>/<blockquote>, nigdy naglowki) - nie we
// wstepie (przed pierwszym <h2>) - nie w sekcjach zakazanych (Zrodla/FAQ/
// Podsumowanie) - nie wewnatrz istniejacego <a> - max 2 linki na sekcje H2 -
// min. 1 akapit odstepu miedzy linkami - self-link pomijany - fragment
// nieznaleziony => skip + log (NIE wymuszamy).
//
// OSLONA [[LNK_nnn]]: optimization + humanization przepuszczaja HTML przez
// LLM bez zadnej kodowej ochrony <a> (historycznie prompty wrecz kazaly linki
// usuwac). protectLinks/restoreLinks tokenizuja WSZYSTKIE <a> przed wysylka
// i odtwarzaja po odpowiedzi. Zachowanie awaryjne:
//   * LLM WYCIAL markery, ale tekst anchora zostal -> ODZYSK: link zakladany
//     ponownie na pierwsze bezpieczne wystapienie tekstu (warning "recovered").
//   * LLM usunal fragment z tekstem -> link przepada; warning "Link lost".
//   * LLM ZDUPLIKOWAL pare -> odtwarzane wszystkie, po czym
//     dedupeInternalLinks sprowadza nadmiarowe do golego tekstu; warning.
//
// Standalone mode: Weaver pomijany w orchestratorze (isStandaloneMode) —
// artykuly standalone celowo nie maja linkow wewnetrznych.
// Pillar: wlasny, niezalezny mechanizm (per-section distribution + P-G retry
// + post-check) — NIETKNIETY; korzysta tylko z oslony [[LNK_nnn]].
// =============================================

import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { getLLMProvider, parseLLMJSON } from "@/services/llm";
import { logDebug, logPublic, logDebugWarn } from "./contentLogger";

// ---------------------------------------------------------------------------
// Typy
// ---------------------------------------------------------------------------

export interface LinkTarget {
  target_slug: string; // "moj-slug" LUB pelny URL (relation_type='wp_existing')
  target_title: string;
  relation_type: string;
}

export interface LinkPlanEntry {
  slug: string;
  fragment: string; // fragment zdania VERBATIM z prozy artykulu
  anchor_text: string; // substring fragmentu
}

export interface AppliedLink {
  slug: string;
  anchor_text: string;
  sectionTitle: string;
  /** "fragment" = pelna sciezka; "anchor-only" = fallback po samym anchorze */
  via?: "fragment" | "anchor-only";
  /** true = anchor plannera byl za dlugi i zostal przyciety do okna <=8 slow */
  trimmed?: boolean;
}

export interface SkippedLink {
  slug: string;
  reason: string;
}

export interface ApplyResult {
  html: string;
  applied: AppliedLink[];
  skipped: SkippedLink[];
}

export interface WeaveResult {
  html: string;
  costUsd: number;
  debug: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Sekcje H2 + bloki prozy (regex z offsetami — konwencja repo, bez DOMParser)
// ---------------------------------------------------------------------------

/** Naglowki, pod ktorymi NIE linkujemy (wzorzec z inContentImagesService). */
const FORBIDDEN_HEADING_FRAGMENTS = [
  "źród", "zrod", "source", "reference", "bibliograf", "literatura",
  "faq", "najczęściej zadawane", "najczesciej zadawane",
  "podsumowan", "wnioski", "summary", "conclusion",
  "spis treści", "spis tresci", "o autorze", "przypis",
];

export function isForbiddenHeading(headingText: string): boolean {
  const lower = headingText.toLowerCase();
  return FORBIDDEN_HEADING_FRAGMENTS.some((f) => lower.includes(f));
}

export interface H2Section {
  title: string;
  start: number; // offset otwarcia <h2>
  end: number; // offset nastepnego <h2> lub koniec dokumentu
  isForbidden: boolean;
}

/** Zakresy sekcji H2 (intro = [0, pierwsze <h2>) — zwracane osobno jako introEnd). */
export function splitByH2Sections(html: string): { introEnd: number; sections: H2Section[] } {
  const sections: H2Section[] = [];
  const re = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
  let m: RegExpExecArray | null;
  const found: Array<{ title: string; start: number }> = [];
  while ((m = re.exec(html)) !== null) {
    found.push({ title: m[1].replace(/<[^>]+>/g, "").trim(), start: m.index });
  }
  for (let i = 0; i < found.length; i++) {
    sections.push({
      title: found[i].title,
      start: found[i].start,
      end: i + 1 < found.length ? found[i + 1].start : html.length,
      isForbidden: isForbiddenHeading(found[i].title),
    });
  }
  return { introEnd: found.length > 0 ? found[0].start : html.length, sections };
}

interface ProseBlock {
  tag: string;
  start: number; // offset poczatku ZAWARTOSCI bloku
  end: number; // offset konca zawartosci
  index: number; // kolejny numer bloku prozy w dokumencie
}

/** Bloki prozy (p/li/blockquote) z offsetami zawartosci — do walidacji i odstepow. */
export function extractProseBlocks(html: string): ProseBlock[] {
  const blocks: ProseBlock[] = [];
  const re = /<(p|li|blockquote)\b[^>]*>([\s\S]*?)<\/\1>/gi;
  let m: RegExpExecArray | null;
  let idx = 0;
  while ((m = re.exec(html)) !== null) {
    const contentStart = m.index + m[0].indexOf(">") + 1;
    blocks.push({ tag: m[1].toLowerCase(), start: contentStart, end: contentStart + m[2].length, index: idx++ });
  }
  return blocks;
}

// ---------------------------------------------------------------------------
// Wyszukiwanie fragmentu w prozie — po TEKSCIE BEZ TAGOW (fix 2026-08-23):
// LLM cytuje zdania jako czysty tekst, a w HTML te same zdania maja w srodku
// <strong>/<i>. Stare doslowne indexOf odrzucalo wtedy 100% propozycji
// ("fragment nieznaleziony" 5/5 w jak-przebiega-ceremonia). Teraz budujemy
// indeks tekstu bez tagow z mapa offsetow do surowego HTML i szukamy w nim.
// ---------------------------------------------------------------------------

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/** Czy pozycja lezy wewnatrz istniejacego <a>...</a>? (skan wstecz) */
function isInsideAnchor(html: string, pos: number): boolean {
  const lastOpen = html.lastIndexOf("<a", pos);
  if (lastOpen === -1) return false;
  const lastClose = html.lastIndexOf("</a>", pos);
  return lastOpen > lastClose;
}

interface PlainIndex {
  text: string; // HTML bez tagow, podstawowe encje zdekodowane
  rawStart: number[]; // rawStart[i] = offset znaku i w surowym HTML
  rawEnd: number[]; // rawEnd[i] = offset konca (exclusive)
}

const PLAIN_ENTITIES: Record<string, string> = {
  "&nbsp;": " ", "&amp;": "&", "&quot;": '"', "&#39;": "'", "&apos;": "'",
  "&ndash;": "–", "&mdash;": "—", "&lt;": "<", "&gt;": ">",
};

function buildPlainIndex(html: string): PlainIndex {
  const chars: string[] = [];
  const rawStart: number[] = [];
  const rawEnd: number[] = [];
  let i = 0;
  while (i < html.length) {
    const ch = html[i];
    if (ch === "<") {
      const close = html.indexOf(">", i);
      if (close === -1) break;
      i = close + 1;
      continue;
    }
    if (ch === "&") {
      const semi = html.indexOf(";", i);
      if (semi !== -1 && semi - i <= 8) {
        const dec = PLAIN_ENTITIES[html.slice(i, semi + 1).toLowerCase()];
        if (dec !== undefined) {
          chars.push(dec);
          rawStart.push(i);
          rawEnd.push(semi + 1);
          i = semi + 1;
          continue;
        }
      }
    }
    chars.push(ch);
    rawStart.push(i);
    rawEnd.push(i + 1);
    i++;
  }
  return { text: chars.join(""), rawStart, rawEnd };
}

/** Regex tolerujacy roznice whitespace: tokeny escapowane, laczone \s+. */
function flexRegex(s: string, flags: string): RegExp | null {
  const tokens = s.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;
  return new RegExp(tokens.map(escapeRegExp).join("\\s+"), flags);
}

/**
 * Czy tagi inline w wycinku HTML sa zbalansowane (kazdy otwarty zamkniety
 * i odwrotnie)? Anchor moze zawierac CALE <strong>x</strong>, ale nie moze
 * przecinac granicy tagu ani zawierac <a>.
 */
function inlineTagsBalanced(seg: string): boolean {
  const stack: string[] = [];
  const re = /<\/?([a-z][a-z0-9]*)[^>]*>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(seg)) !== null) {
    const tag = m[1].toLowerCase();
    if (tag === "a") return false; // link w linku
    if (m[0].startsWith("</")) {
      if (stack.pop() !== tag) return false;
    } else if (!m[0].endsWith("/>") && tag !== "br" && tag !== "img" && tag !== "hr") {
      stack.push(tag);
    }
  }
  return stack.length === 0;
}

export interface FragmentMatch {
  start: number; // surowy zakres calego fragmentu
  end: number;
  anchorStart: number; // surowy zakres anchora do owiniecia <a>
  anchorEnd: number;
}

/**
 * Znajdz fragment w prozie artykulu (dopasowanie po tekscie bez tagow).
 * Zwraca null, gdy fragmentu nie ma albo lezy w miejscu zabronionym
 * (naglowek/wstep/sekcja zakazana/wewnatrz <a>) albo anchor przecina granice
 * formatowania. NIE wymuszamy — brak trafienia to skip, nie blad.
 */
export function findFragmentInProse(
  html: string,
  fragment: string,
  blocks: ProseBlock[],
  introEnd: number,
  sections: H2Section[],
  anchor?: string,
): FragmentMatch | null {
  const frag = (fragment || "").trim();
  if (frag.length < 12) return null; // za krotkie = zbyt ryzykowne dopasowanie
  if (frag.includes("<")) return null; // LLM ma cytowac czysty tekst

  const idx = buildPlainIndex(html);
  // "gi": model czesto zmienia wielkosc pierwszej litery (poczatek zdania vs
  // srodek) — pozycje i tak bierzemy z realnego tekstu, wiec case-insensitive
  // dopasowanie jest bezpieczne
  const fragRe = flexRegex(frag, "gi");
  if (!fragRe) return null;

  let m: RegExpExecArray | null;
  while ((m = fragRe.exec(idx.text)) !== null) {
    const plainStart = m.index;
    const plainEnd = m.index + m[0].length;
    if (plainEnd <= plainStart) break;
    const rawS = idx.rawStart[plainStart];
    const rawE = idx.rawEnd[plainEnd - 1];

    // musi lezec W CALOSCI wewnatrz jednego bloku prozy (odrzuca naglowki,
    // td, zakresy przez granice akapitow)
    const block = blocks.find((b) => rawS >= b.start && rawE <= b.end);
    if (!block) continue;
    if (rawS < introEnd) continue; // nie we wstepie
    const section = sections.find((s) => rawS >= s.start && rawS < s.end);
    if (section?.isForbidden) continue; // nie w sekcji zakazanej
    if (isInsideAnchor(html, rawS)) continue; // nie wewnatrz istniejacego <a>

    // anchor: zmapuj na surowe offsety i sprawdz, czy da sie bezpiecznie owinac
    let aRawS = rawS;
    let aRawE = rawE;
    if (anchor && anchor.trim()) {
      const aRe = flexRegex(anchor, "i");
      const am = aRe ? aRe.exec(m[0]) : null;
      if (!am) continue;
      const aPlainStart = plainStart + am.index;
      const aPlainEnd = aPlainStart + am[0].length;
      aRawS = idx.rawStart[aPlainStart];
      aRawE = idx.rawEnd[aPlainEnd - 1];
      const seg = html.slice(aRawS, aRawE);
      if (!inlineTagsBalanced(seg)) continue; // anchor przecina granice tagu
      if (isInsideAnchor(html, aRawS)) continue;
    }
    return { start: rawS, end: rawE, anchorStart: aRawS, anchorEnd: aRawE };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Deterministyczna aplikacja planu
// ---------------------------------------------------------------------------

/** href dla targetu: wp_existing = pelny URL bez zmian; inaczej "/slug". */
function hrefFor(slug: string): string {
  if (/^https?:\/\//i.test(slug)) return slug;
  return slug.startsWith("/") ? slug : `/${slug}`;
}

/** Znormalizowany klucz targetu do porownan (dedupe/self-guard). */
function slugKey(slugOrHref: string): string {
  let s = slugOrHref.trim().toLowerCase();
  s = s.replace(/^https?:\/\/[^/]+/, "");
  s = s.split(/[?#]/)[0];
  s = s.replace(/\/+$/, "");
  s = s.replace(/^\//, "");
  return s;
}

const MAX_ANCHOR_WORDS = 8;
const MAX_ANCHOR_CHARS = 80;

/** Fold diakrytykow do porownan slug-slowo ("pojęć" -> "pojec"). */
function foldDiacritics(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ł/g, "l");
}

const SLUG_STOPWORDS = new Set([
  "przy", "jako", "oraz", "jak", "czym", "czy", "ktory", "ktora", "ktore",
  "jakie", "gdzie", "kiedy", "tego", "temu", "jest", "moga", "sie",
]);

/** Slowa-klucze ze sluga do scoringu okna anchora. */
function slugKeywords(slug: string): string[] {
  return slugKey(slug)
    .split(/[/-]/)
    .map((w) => foldDiacritics(w))
    .filter((w) => w.length >= 4 && !SLUG_STOPWORDS.has(w));
}

/**
 * Przycina za dlugi anchor plannera do najlepszego okna 2-8 KOLEJNYCH slow.
 * (2026-08-24: planner gpt-4.1-mini notorycznie dawal 9-14 slow mimo promptu
 * "2-6" -> twardy cap odrzucal wpisy i artykuly wychodzily bez linkow.)
 * Anchor jest doslownym wycinkiem prozy, wiec kazdy ciagly pod-fragment to
 * nadal ten sam tekst zdania — wybor okna zmienia tylko zasieg klikalnosci.
 * Scoring: trafienia slow-kluczy sluga (x3) minus interpunkcja w srodku;
 * remis -> dluzsze okno, potem pozniejsze (polskie frazy trzymaja rdzen na koncu).
 */
export function trimAnchorToCap(anchor: string, slug: string): string | null {
  const clean = anchor.trim().replace(/\s+/g, " ");
  const words = clean.split(" ");
  if (words.length >= 2 && words.length <= MAX_ANCHOR_WORDS && clean.length <= MAX_ANCHOR_CHARS) {
    return clean;
  }
  if (words.length < 2) return null;

  const keys = slugKeywords(slug);
  const wordScore = (w: string): number => {
    const f = foldDiacritics(w.replace(/[^\p{L}\p{N}]+/gu, ""));
    if (f.length < 3) return 0;
    return keys.some((k) => f.startsWith(k.slice(0, 5)) || k.startsWith(f.slice(0, 5))) ? 1 : 0;
  };

  let best: { text: string; score: number; size: number; start: number } | null = null;
  for (let size = Math.min(MAX_ANCHOR_WORDS, words.length); size >= 2; size--) {
    for (let start = 0; start + size <= words.length; start++) {
      const slice = words.slice(start, start + size);
      // interpunkcja brzegowa odpada (anchor "porcji," wyglada na uciety)
      const text = slice.join(" ").replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
      const outWords = text.split(/\s+/).filter(Boolean);
      if (outWords.length < 2 || outWords.length > MAX_ANCHOR_WORDS || text.length > MAX_ANCHOR_CHARS) continue;
      const hits = slice.reduce((acc, w) => acc + wordScore(w), 0);
      const punct = (text.match(/[,;:]/g) || []).length;
      const score = hits * 3 - punct;
      if (
        !best ||
        score > best.score ||
        (score === best.score && size > best.size) ||
        (score === best.score && size === best.size && start > best.start)
      ) {
        best = { text, score, size, start };
      }
    }
  }
  return best ? best.text : null;
}

export function applyLinkPlan(
  html: string,
  plan: LinkPlanEntry[],
  opts: { selfSlug?: string; maxPerSection?: number; minParagraphGap?: number } = {},
): ApplyResult {
  const maxPerSection = opts.maxPerSection ?? 2;
  const minGap = opts.minParagraphGap ?? 1;
  const selfKey = opts.selfSlug ? slugKey(opts.selfSlug) : null;

  const { introEnd, sections } = splitByH2Sections(html);
  const blocks = extractProseBlocks(html);

  const usedTargets = new Set<string>();
  const perSectionCount = new Map<number, number>(); // index sekcji -> liczba linkow
  const usedParagraphIdx: number[] = [];

  const applied: AppliedLink[] = [];
  const skipped: SkippedLink[] = [];
  const placements: Array<{ match: FragmentMatch; entry: LinkPlanEntry }> = [];

  for (const entry of plan || []) {
    const slug = (entry?.slug || "").trim();
    const fragment = (entry?.fragment || "").trim();
    const anchor = (entry?.anchor_text || "").trim();

    if (!slug || !fragment || !anchor) {
      skipped.push({ slug: slug || "(pusty)", reason: "niekompletny wpis planu" });
      continue;
    }
    const key = slugKey(slug);
    if (selfKey && key === selfKey) {
      skipped.push({ slug, reason: "self-link" });
      continue;
    }
    if (usedTargets.has(key)) {
      skipped.push({ slug, reason: "target juz uzyty (max 1x na artykul)" });
      continue;
    }
    // Twardy limit dlugosci anchora W KODZIE (prompt mowi 2-6 slow, ale substring-check
    // przepuszczal anchor = caly fragment -> incydent "podlinkowane cale zdanie w <sup>").
    // AUTO-TRIM (2026-08-24): planner i tak dawal 9-14 slow, a skip wycinal linki
    // z calych artykulow (Bufet: 0/3) — za dlugi anchor przycinamy do okna <=8 slow.
    let anchorText = anchor;
    let wasTrimmed = false;
    let anchorWords = anchorText.split(/\s+/).length;
    if (anchorWords > MAX_ANCHOR_WORDS || anchorText.length > MAX_ANCHOR_CHARS) {
      const trimmed = trimAnchorToCap(anchorText, slug);
      if (trimmed && trimmed.length > 0) {
        wasTrimmed = trimmed !== anchorText;
        anchorText = trimmed;
        anchorWords = anchorText.split(/\s+/).length;
      }
    }
    if (anchorWords < 2 || anchorWords > MAX_ANCHOR_WORDS || anchorText.length > MAX_ANCHOR_CHARS) {
      skipped.push({ slug, reason: `anchor poza limitem 2-8 slow / 80 znakow (${anchorWords} slow, trim nieudany)` });
      continue;
    }

    // substring-check (whitespace- i case-tolerant) — nie odrzuca, tylko decyduje
    // czy fragment jest wiarygodny; parafrazy ida od razu do fallbacku
    const normWs = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
    const fragmentTrusted = normWs(fragment).includes(normWs(anchorText));

    let match = fragmentTrusted
      ? findFragmentInProse(html, fragment, blocks, introEnd, sections, anchorText)
      : null;
    let via: "fragment" | "anchor-only" = "fragment";
    if (!match) {
      // FALLBACK (2026-08-23 noc): gpt-4.1-mini czesto PARAFRAZUJE fragment
      // (albo daje anchor spoza niego) — wtedy szukamy w prozie SAMEGO anchora,
      // z tymi samymi wszystkimi strazami (findFragmentInProse: proza-only,
      // nie wstep, nie sekcja zakazana, nie w <a>, balans tagow, min 12 znakow)
      match = findFragmentInProse(html, anchorText, blocks, introEnd, sections, anchorText);
      via = "anchor-only";
    }
    if (!match) {
      skipped.push({
        slug,
        reason: fragmentTrusted
          ? "fragment nieznaleziony w prozie, fallback po anchorze tez nieudany (miejsce zabronione / formatowanie / <12 znakow)"
          : "anchor spoza fragmentu (parafraza) i nieznaleziony w prozie",
      });
      continue;
    }
    // kolizja z wczesniej zaakceptowanym miejscem
    if (placements.some((p) => match.start < p.match.end && p.match.start < match.end)) {
      skipped.push({ slug, reason: "fragment nachodzi na wczesniejszy link" });
      continue;
    }

    const sectionIdx = sections.findIndex((s) => match.start >= s.start && match.start < s.end);
    const count = perSectionCount.get(sectionIdx) ?? 0;
    if (count >= maxPerSection) {
      skipped.push({ slug, reason: `limit ${maxPerSection} linkow na sekcje H2` });
      continue;
    }

    const block = blocks.find((b) => match.start >= b.start && match.end <= b.end)!;
    // minGap=1 => ten sam akapit (roznica 0) i sasiedni (roznica 1) sa za blisko
    if (usedParagraphIdx.some((u) => Math.abs(u - block.index) <= minGap)) {
      skipped.push({ slug, reason: "za blisko poprzedniego linku (min. 1 akapit odstepu)" });
      continue;
    }

    usedTargets.add(key);
    perSectionCount.set(sectionIdx, count + 1);
    usedParagraphIdx.push(block.index);
    placements.push({ match, entry });
    applied.push({
      slug,
      anchor_text: anchorText,
      sectionTitle: sectionIdx >= 0 ? sections[sectionIdx].title : "(intro?)",
      via,
      ...(wasTrimmed ? { trimmed: true } : {}),
    });
  }

  // aplikacja od KONCA — wczesniejsze offsety pozostaja wazne; owijamy zakres
  // anchora w surowym HTML (moze zawierac CALE tagi inline — zbalansowane,
  // sprawdzone w findFragmentInProse)
  placements.sort((a, b) => b.match.anchorStart - a.match.anchorStart);
  let out = html;
  for (const p of placements) {
    const { match, entry } = p;
    out =
      out.slice(0, match.anchorStart) +
      `<a href="${escapeAttr(hrefFor(entry.slug))}">` +
      out.slice(match.anchorStart, match.anchorEnd) +
      `</a>` +
      out.slice(match.anchorEnd);
  }

  return { html: out, applied, skipped };
}

// ---------------------------------------------------------------------------
// Dedupe — niezalezna siatka bezpieczenstwa (pierwszy zostaje, kolejne -> tekst)
// ---------------------------------------------------------------------------

const ANY_LINK_RE = /<a\s+[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi;

export function dedupeInternalLinks(html: string): { html: string; removed: number } {
  const seen = new Set<string>();
  let removed = 0;
  const out = html.replace(ANY_LINK_RE, (full, href: string, inner: string) => {
    // dedupe TYLKO linkow wewnetrznych (relative "/...") — external/anchor/sup zostaja
    if (!href.startsWith("/")) return full;
    const key = slugKey(href);
    if (seen.has(key)) {
      removed++;
      return inner; // odlinkowanie: zostaje czysty tekst (z ew. <em>/<strong>)
    }
    seen.add(key);
    return full;
  });
  return { html: out, removed };
}

// ---------------------------------------------------------------------------
// Oslona linkow dla optimization/humanization — MARKERY PARZYSTE
//
// v1 (nieprzezroczysty [[LNK_nnn]] zamiast calego <a>) zawiodl w praktyce
// (incydent 2026-08-23, pierwsze artykuly po wdrozeniu):
//  * humanizacja PRZEPISALA zdanie wokol placeholdera nie widzac jego tresci
//    -> gramatyka rozjechana ("...zaczyna sie Najpierw trzy rzeczy"),
//  * model potraktowal token jak znacznik przypisu i owinal go w <sup>.
// v2: tekst anchora zostaje WIDOCZNY miedzy markerami
//    [[LNK_001]]tekst linku[[/LNK_001]]
// — model pisze wokol slow naturalnie, a restore owija tekst (nawet lekko
// zredagowany przez model) z powrotem w oryginalny tag <a>.
// ---------------------------------------------------------------------------

export interface LinkProtectionMap {
  /** id ("001") -> { openTag: '<a href="...">', href, inner (tekst anchora) } */
  entries: Map<string, { openTag: string; href: string; inner: string }>;
}

/** Chroni WSZYSTKIE <a>...</a> (internal, external, #zrodlo) markerami parzystymi. */
export function protectLinks(html: string): { html: string; map: LinkProtectionMap } {
  const map: LinkProtectionMap = { entries: new Map() };
  const out = html.replace(ANY_LINK_RE, (full, href: string, inner: string) => {
    const id = String(map.entries.size + 1).padStart(3, "0");
    const openTag = full.slice(0, full.indexOf(">") + 1);
    map.entries.set(id, { openTag, href, inner });
    return `[[LNK_${id}]]${inner}[[/LNK_${id}]]`;
  });
  return { html: out, map };
}

/** Czy pozycja lezy wewnatrz naglowka h1-h6? (skan wstecz po ostatnim tagu h*) */
function isInsideHeading(html: string, pos: number): boolean {
  const before = html.slice(0, pos);
  const re = /<(\/?)h[1-6][^>]*>/gi;
  let last: RegExpExecArray | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(before)) !== null) last = m;
  return last !== null && last[1] !== "/";
}

/**
 * Odzysk linku po tekscie anchora (incydent 2026-08-23 wieczor: optimization
 * wycial WSZYSTKIE markery [[LNK]] mimo noty, ale tekst anchorow zostawil —
 * artykul stracil linki i przypisy). Skoro v2 trzyma tekst widoczny, to gdy
 * para markerow znikla a tekst nadal jest w odpowiedzi — zakladamy link
 * z powrotem na pierwsze bezpieczne wystapienie tekstu.
 */
/** Czy pozycja lezy wewnatrz JESZCZE-NIEODTWORZONEJ pary markerow [[LNK_...]]?
 *  (incydent 2026-08-24 "Slownik cateringu": zgubiona para 00X z innerem "[1]"
 *  odzyskala sie NA TEKSCIE innej, ocalalej pary o tym samym innerze -> po jej
 *  restore powstal zagniezdzony <a><a>[1]</a></a>). */
function isInsideMarkerPair(html: string, pos: number): boolean {
  const lastOpen = html.lastIndexOf("[[LNK_", pos);
  const lastClose = html.lastIndexOf("[[/LNK_", pos);
  return lastOpen !== -1 && lastOpen > lastClose;
}

function tryRecoverLink(html: string, openTag: string, inner: string): string | null {
  const needle = inner.trim();
  if (!needle || needle.length < 2 || needle.includes("<")) return null;

  const safeAt = (i: number): boolean => {
    // nie wewnatrz tagu (np. w atrybucie href)
    if (html.lastIndexOf("<", i) > html.lastIndexOf(">", i)) return false;
    // nie wewnatrz istniejacego <a> (takze linku odzyskanego chwile wczesniej)
    if (isInsideAnchor(html, i)) return false;
    // nie wewnatrz nieodtworzonej pary markerow (ten tekst dostanie swoj <a> za chwile)
    if (isInsideMarkerPair(html, i)) return false;
    // nie w naglowku
    if (isInsideHeading(html, i)) return false;
    return true;
  };

  // 1) exact match
  let from = 0;
  while (from < html.length) {
    const i = html.indexOf(needle, from);
    if (i === -1) break;
    from = i + 1;
    if (!safeAt(i)) continue;
    return html.slice(0, i) + openTag + needle + "</a>" + html.slice(i + needle.length);
  }

  // 2) fallback flex (case/whitespace-insensitive) — humanizacja czesto lekko
  //    redaguje tekst anchora (wielkosc liter, podwojne spacje); exact wtedy
  //    nie trafia i link przepadal (incydent "Dekoracje kosciola": 3 -> 2 linki)
  const flex = flexRegex(needle, "gi");
  if (flex) {
    let m: RegExpExecArray | null;
    while ((m = flex.exec(html)) !== null) {
      const i = m.index;
      if (!safeAt(i)) continue;
      return html.slice(0, i) + openTag + m[0] + "</a>" + html.slice(i + m[0].length);
    }
  }

  // 3) fallback po POD-FRAZIE (2026-08-24, "Slownik": humanizacja przepisala cale
  //    zdania i 2/3 linkow przepadlo) — parafraza zwykle zostawia rdzen frazy
  //    ("serwis kelnerski", "menu weselne"). Szukamy najdluzszego okna >=2 slow
  //    (>=10 znakow) z oryginalnego anchora; najdluzsze okna maja priorytet,
  //    zeby nie zlapac przypadkiem generycznej 2-slowki w zlym miejscu.
  const needleWords = needle.split(/\s+/).filter(Boolean);
  if (needleWords.length >= 3) {
    for (let size = needleWords.length - 1; size >= 2; size--) {
      for (let start = 0; start + size <= needleWords.length; start++) {
        const sub = needleWords.slice(start, start + size).join(" ");
        if (sub.length < 10) continue;
        const rx = flexRegex(sub, "gi");
        if (!rx) continue;
        let mm: RegExpExecArray | null;
        while ((mm = rx.exec(html)) !== null) {
          const i = mm.index;
          if (!safeAt(i)) continue;
          return html.slice(0, i) + openTag + mm[0] + "</a>" + html.slice(i + mm[0].length);
        }
      }
    }
  }
  return null;
}

/**
 * Nota techniczna doklejana W KODZIE do promptow optimization/humanization,
 * gdy HTML zawiera markery osłony. Celowo poza DB (prompty w DB sa edytowalne
 * i niewersjonowane — ochrona markerow nie moze od nich zalezec).
 */
export const LINK_MARKER_NOTE = `

## MARKERY TECHNICZNE [[LNK_nnn]] (KRYTYCZNE)
Fragmenty w formie [[LNK_nnn]]tekst[[/LNK_nnn]] to linki wewnetrzne w oslonie technicznej:
- NIE usuwaj, NIE przenos i NIE duplikuj markerow otwierajacych ani zamykajacych;
- tekst MIEDZY markerami mozesz lekko redagowac, ale musi zostac miedzy nimi i plynac naturalnie w zdaniu;
- to NIE sa przypisy ani cytaty — NIE owijaj ich w <sup> i nie zamieniaj w odnosniki zrodlowe;
- w odpowiedzi zwroc markery dokladnie w tej samej formie.`;

/** Dokleja LINK_MARKER_NOTE do prompta tylko, gdy HTML faktycznie zawiera markery. */
export function withLinkMarkerNote(prompt: string, protectedHtml: string): string {
  return protectedHtml.includes("[[LNK_") ? prompt + LINK_MARKER_NOTE : prompt;
}

/**
 * Odtwarza linki po odpowiedzi LLM. Zachowanie awaryjne:
 *  - zgubiona PARA markerow -> link przepada, warning "Link lost" (bez reinjectu);
 *  - osierocony pojedynczy marker -> zdejmowany (tekst zostaje), warning;
 *  - zduplikowana para -> odtwarzane wszystkie, dedupeInternalLinks sprowadza
 *    nadmiarowe internal do tekstu, warning "collapsed";
 *  - <sup> owiniety przez model wokol linku wewnetrznego -> zdejmowany
 *    (lekcja z incydentu: link w superskrypcie wyglada jak przypis).
 */
export function restoreLinks(html: string, map: LinkProtectionMap): { html: string; warnings: string[] } {
  const warnings: string[] = [];
  let out = html;

  for (const [id, { openTag, href, inner }] of map.entries) {
    const pair = new RegExp(`\\[\\[LNK_${id}\\]\\]([\\s\\S]*?)\\[\\[/LNK_${id}\\]\\]`, "g");
    let count = 0;
    out = out.replace(pair, (_full, pairInner: string) => {
      count++;
      return `${openTag}${pairInner}</a>`;
    });
    if (count === 0) {
      // brak pary — moze zostal pojedynczy marker (model zgubil polowke)
      const strayOpen = out.indexOf(`[[LNK_${id}]]`) !== -1;
      const strayClose = out.indexOf(`[[/LNK_${id}]]`) !== -1;
      if (strayOpen || strayClose) {
        out = out.split(`[[LNK_${id}]]`).join("").split(`[[/LNK_${id}]]`).join("");
      }
      // ODZYSK: model wycial markery, ale tekst anchora czesto zostaje —
      // zaloz link z powrotem na pierwsze bezpieczne wystapienie tekstu.
      const recovered = tryRecoverLink(out, openTag, inner);
      if (recovered !== null) {
        out = recovered;
        warnings.push(`Link recovered: ${href} (model wycial markery LNK_${id} — link zalozony ponownie po tekscie)`);
      } else if (strayOpen || strayClose) {
        warnings.push(`Link lost: ${href} (model zgubil polowke markera LNK_${id} — tekst zostal, link nie)`);
      } else {
        warnings.push(`Link lost: ${href} (model usunal fragment z markerem LNK_${id})`);
      }
      continue;
    }
    if (count > 1) {
      warnings.push(`Link duplicated by LLM: ${href} (${count}x para LNK_${id} — collapsed)`);
    }
  }

  // zagniezdzony <a> w <a> (invalid HTML — relikt recovery sprzed guarda
  // markerow) -> zostaje WEWNETRZNY, kompletny link
  out = out.replace(/<a\s+[^>]*>\s*(<a\s+[^>]*>[\s\S]*?<\/a>)\s*<\/a>/gi, "$1");

  // duplikaty z powielonych par (tylko internal) -> goly tekst
  const deduped = dedupeInternalLinks(out);
  if (deduped.removed > 0) out = deduped.html;

  // <sup> doklejony przez model wokol linku WEWNETRZNEGO -> zdejmij (to nie przypis)
  out = out.replace(/<sup>\s*(<a\s+[^>]*href="\/[^"]*"[^>]*>[\s\S]*?<\/a>)\s*<\/sup>/gi, "$1");

  // resztki markerow, ktorych nie ma w mapie (model je zmyslil) -> wytnij
  out = out.replace(/\[\[\/?LNK_\d{3}\]\]/g, "");
  return { html: out, warnings };
}

// ---------------------------------------------------------------------------
// Warstwa LLM — weaveLinks
// ---------------------------------------------------------------------------

const WEAVE_SCHEMA = {
  type: "object",
  properties: {
    links: {
      type: "array",
      items: {
        type: "object",
        properties: {
          slug: { type: "string" },
          fragment: { type: "string" },
          anchor_text: { type: "string" },
        },
        required: ["slug", "fragment", "anchor_text"],
        additionalProperties: false,
      },
    },
  },
  required: ["links"],
  additionalProperties: false,
};

const WEAVER_FALLBACK_PROMPT = `Jestes redaktorem odpowiedzialnym za linkowanie wewnetrzne. Dostajesz gotowy artykul HTML i liste artykulow docelowych. Dla kazdego targetu (jesli ma naturalne miejsce) wskaz JEDEN fragment zdania przepisany DOSLOWNIE z tresci artykulu (z akapitow, nigdy z naglowkow ani wstepu) oraz anchor_text bedacy 2-6 wyrazowym fragmentem tego zdania. Rozne targety umieszczaj w roznych sekcjach. Jesli target nie pasuje nigdzie naturalnie — pomin go. Zwroc TYLKO JSON: {"links":[{"slug":"...","fragment":"...","anchor_text":"..."}]}.

## Artykul (HTML):
{{article_html}}

## Targety:
{{link_targets_formatted}}

## Jezyk: {{language}}`;

export async function weaveLinks(
  html: string,
  targets: LinkTarget[],
  opts: { language: string; selfSlug?: string; nodeId?: string },
): Promise<WeaveResult> {
  const debug: Record<string, unknown> = { targets_in: targets?.length ?? 0 };

  if (!targets || targets.length === 0) {
    // dedupe i tak — siatka niezalezna od LLM
    const deduped = dedupeInternalLinks(html);
    debug.skipped_reason = "brak targetow";
    debug.dedupe_removed = deduped.removed;
    return { html: deduped.html, costUsd: 0, debug };
  }

  const targetsFormatted = targets
    .map((t) => {
      const href = t.relation_type === "wp_existing" ? t.target_slug : `/${t.target_slug}`;
      return `- ${href} — ${t.target_title} (${t.relation_type})`;
    })
    .join("\n");

  let costUsd = 0;
  let plan: LinkPlanEntry[] = [];

  try {
    const dbPrompt = await loadPrompt("link_weaving");
    const template = dbPrompt?.template || WEAVER_FALLBACK_PROMPT;
    const prompt = fillTemplate(template, {
      article_html: html,
      link_targets_formatted: targetsFormatted,
      language: opts.language,
    });

    const model = dbPrompt?.modelId || "gpt-4.1-mini";
    logDebug("LinkWeaver", "Call", {
      step: "link_weaving",
      model,
      targets: targets.length,
      prompt_length: prompt.length,
    });

    const provider = getLLMProvider("openai");
    const response = await provider.call(prompt, {
      model,
      temperature: dbPrompt?.temperature ?? 0.2,
      maxTokens: 1500,
      jsonSchema: WEAVE_SCHEMA,
      step: "link_weaving",
      nodeId: opts.nodeId,
    });
    costUsd = response.costUsd ?? 0;

    const parsed = parseLLMJSON<{ links: LinkPlanEntry[] }>(response.text);
    plan = Array.isArray(parsed?.links) ? parsed.links : [];
  } catch (err) {
    // FAIL-SOFT: artykul bez linkow > artykul zepsuty / pipeline przerwany
    logDebugWarn("LinkWeaver", `LLM/parsowanie nieudane — artykul zostaje bez wplecionych linkow: ${err instanceof Error ? err.message : err}`);
    debug.error = err instanceof Error ? err.message : String(err);
    const deduped = dedupeInternalLinks(html);
    debug.dedupe_removed = deduped.removed;
    return { html: deduped.html, costUsd, debug };
  }

  const result = applyLinkPlan(html, plan, { selfSlug: opts.selfSlug });
  let outHtml = result.html;
  let applied = [...result.applied];
  let skipped = [...result.skipped];

  // RETRY (2026-08-24): planner czesto PARAFRAZUJE fragment/anchor i wpis odpada
  // ("Bufet": 2/3 targetow odrzucone jako nieznalezione). Jedno drugie podejscie
  // TYLKO dla odrzuconych targetow, z twarda uwaga "kopiuj znak w znak".
  const RETRYABLE = /nieznaleziony|parafraza/i;
  const retrySlugKeys = new Set(skipped.filter((s) => RETRYABLE.test(s.reason)).map((s) => slugKey(s.slug)));
  const retryTargets = targets.filter((t) => {
    const href = t.relation_type === "wp_existing" ? t.target_slug : `/${t.target_slug}`;
    return retrySlugKeys.has(slugKey(href));
  });
  if (retryTargets.length > 0) {
    try {
      const dbPrompt = await loadPrompt("link_weaving");
      const template = dbPrompt?.template || WEAVER_FALLBACK_PROMPT;
      const retryFormatted = retryTargets
        .map((t) => {
          const href = t.relation_type === "wp_existing" ? t.target_slug : `/${t.target_slug}`;
          return `- ${href} — ${t.target_title} (${t.relation_type})`;
        })
        .join("\n");
      const rejectedList = skipped
        .filter((s) => RETRYABLE.test(s.reason))
        .map((s) => `- ${s.slug}: ${s.reason}`)
        .join("\n");
      const retryPrompt =
        fillTemplate(template, {
          article_html: outHtml,
          link_targets_formatted: retryFormatted,
          language: opts.language,
        }) +
        `\n\n## RETRY — poprzedni plan ODRZUCONY przez walidator:\n${rejectedList}\nPowod odrzucenia: fragment/anchor NIE byl doslowna kopia tekstu artykulu. Tym razem skopiuj fragment i anchor ZNAK W ZNAK z artykulu (jak Ctrl+C/Ctrl+V), z akapitu tresci. Anchor 2-6 kolejnych slow z fragmentu. Jesli dla targetu nie ma naturalnego zdania — pomin go.`;

      const provider = getLLMProvider("openai");
      const retryResp = await provider.call(retryPrompt, {
        model: dbPrompt?.modelId || "gpt-4.1-mini",
        temperature: dbPrompt?.temperature ?? 0.2,
        maxTokens: 1500,
        jsonSchema: WEAVE_SCHEMA,
        step: "link_weaving",
        nodeId: opts.nodeId,
      });
      costUsd += retryResp.costUsd ?? 0;
      const retryParsed = parseLLMJSON<{ links: LinkPlanEntry[] }>(retryResp.text);
      const retryPlan = Array.isArray(retryParsed?.links) ? retryParsed.links : [];
      if (retryPlan.length > 0) {
        const retryResult = applyLinkPlan(outHtml, retryPlan, { selfSlug: opts.selfSlug });
        if (retryResult.applied.length > 0) {
          outHtml = retryResult.html;
          applied = [...applied, ...retryResult.applied];
          const rescued = new Set(retryResult.applied.map((a) => slugKey(a.slug)));
          skipped = skipped.filter((s) => !rescued.has(slugKey(s.slug)));
        }
        debug.retry = {
          targets: retryTargets.length,
          plan_entries: retryPlan.length,
          applied: retryResult.applied.length,
        };
      } else {
        debug.retry = { targets: retryTargets.length, plan_entries: 0, applied: 0 };
      }
    } catch (err) {
      // FAIL-SOFT — retry to bonus, nie warunek
      logDebugWarn("LinkWeaver", `Retry planu nieudany: ${err instanceof Error ? err.message : err}`);
      debug.retry_error = err instanceof Error ? err.message : String(err);
    }
  }

  const deduped = dedupeInternalLinks(outHtml);

  debug.plan_entries = plan.length;
  debug.applied = applied.map(
    (a) => `${a.slug} @ "${a.sectionTitle}"${a.via === "anchor-only" ? " [fallback:anchor]" : ""}${a.trimmed ? " [trim]" : ""}`,
  );
  debug.skipped = skipped;
  debug.dedupe_removed = deduped.removed;

  logPublic(
    "LinkWeaver",
    `Wpleciono ${applied.length}/${targets.length} linkow` +
      (skipped.length ? ` (pominieto ${skipped.length}: ${skipped.map((s) => s.reason).slice(0, 3).join("; ")}${skipped.length > 3 ? "…" : ""})` : ""),
  );

  return { html: deduped.html, costUsd, debug };
}
